import { RoutesService, eLayoutType } from '@abp/ng.core';
import { inject, provideAppInitializer } from '@angular/core';
import { AppPathConsts } from './shared/AppPathConsts';
import { AppPermissionConsts } from './shared/AppPermissionConsts';

export const APP_ROUTE_PROVIDER = [
  provideAppInitializer(() => {
    configureRoutes();
  }),
];

function configureRoutes() {
  const routes = inject(RoutesService);
  routes.add([
    {
      path: '/',
      name: '::Menu:Home',
      iconClass: 'fas fa-home',
      order: 1,
      layout: eLayoutType.application,
    },
    ...serviceEntrySheetGenerationRoutes(2),
    ...masterRoutes(3),
    ...simulatorRoute(4),
  ]);
}

const serviceEntrySheetGenerationRoutes = (parentOrder: number) => {
  const parentNameInternal = '::ServiceEntrySheetGenerationRoot';
  const children = [
    {
      path: `/${AppPathConsts.ServiceEntrySheet}/${AppPathConsts.ServiceEntrySheetGeneration}`,
      name: '::ServiceEntrySheetGeneration',
      order: 1,
      requiredPolicy: AppPermissionConsts.ServiceEntrySheetGenerationViewPendingOrderLines,
      iconClass: 'pi pi-check-square',
      eLayoutType: eLayoutType.application,
      parentName: parentNameInternal,
    },
    {
      path: `/${AppPathConsts.ServiceEntrySheet}`,
      name: '::ServiceEntrySheetVisualization',
      order: 2,
      requiredPolicy: AppPermissionConsts.ServiceEntrySheetsDefault,
      iconClass: 'pi pi-eye',
      eLayoutType: eLayoutType.application,
      parentName: parentNameInternal,
    },
    {
      path: `/${AppPathConsts.ServiceEntrySheet}/${AppPathConsts.IntegrationMonitor}`,
      name: '::IntegrationMonitor',
      order: 3,
      requiredPolicy: AppPermissionConsts.IntegrationMonitorDefault,
      iconClass: 'pi pi-desktop',
      eLayoutType: eLayoutType.application,
      parentName: parentNameInternal,
    },
  ];
  return [
    {
      path: AppPathConsts.ServiceEntrySheetPrefix,
      name: parentNameInternal,
      displayName: '::ServiceEntrySheetGeneration',
      iconClass: 'pi pi-folder',
      order: parentOrder,
      requiredPolicy: AppPermissionConsts.ServiceEntrySheetGenerationDefault,
    },
    ...children,
  ];
};

const masterRoutes = (parentOrder: number) => {
  const children = [
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.ServiceConfigurations}`,
      name: '::ServiceConfigurations',
      order: 1,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ServiceConfigurationDefault,
      iconClass: 'pi pi-sliders-h',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.Periods}`,
      name: '::Periods',
      order: 2,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.PeriodDefault,
      iconClass: 'pi pi-calendar',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.Providers}`,
      name: '::Providers',
      order: 3,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ProviderDefault,
      iconClass: 'pi pi-briefcase',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.ProviderConfigurations}`,
      name: '::ProviderConfigurations',
      order: 4,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ProviderConfigurationDefault,
      iconClass: 'pi pi-sort-alt',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.UserProviders}`,
      name: '::UserProviders',
      order: 5,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.UserProviderDefault,
      iconClass: 'pi pi-users',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.Services}`,
      name: '::Services',
      order: 6,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ServiceDefault,
      iconClass: 'pi pi-tags',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.ServiceTypes}`,
      name: '::ServiceTypes',
      order: 7,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ServiceTypeDefault,
      iconClass: 'pi pi-th-large',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.ExternalSystems}`,
      name: '::ExternalSystems',
      order: 8,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ExternalSystemDefault,
      iconClass: 'pi pi-objects-column',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.Vendors}`,
      name: '::Vendors',
      order: 9,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.VendorDefault,
      iconClass: 'pi pi-building',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.Actions}`,
      name: '::Actions',
      order: 10,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.ActionDefault,
      iconClass: 'pi pi-bolt',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.Places}`,
      name: '::Places',
      order: 11,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.PlaceDefault,
      iconClass: 'pi pi-map-marker',
      eLayoutType: eLayoutType.application,
    },
    {
      path: `/${AppPathConsts.MastersPrefix}/${AppPathConsts.RoleVendors}`,
      name: '::RoleVendors',
      order: 12,
      parentName: '::Masters',
      requiredPolicy: AppPermissionConsts.RoleVendorDefault,
      iconClass: 'pi pi-globe',
      eLayoutType: eLayoutType.application,
    },
  ];

  return [
    {
      path: AppPathConsts.MastersPrefix,
      name: '::Masters',
      iconClass: 'pi pi-cog',
      order: parentOrder,
      requiredPolicy: AppPermissionConsts.MasterPermission,
    },
    ...children,
  ];
};

function simulatorRoute(parentOrder: number) {
  const children = [
    {
      path: `/${AppPathConsts.ExternalPrefix}/${AppPathConsts.CreateOrderLineSimulator}`,
      name: '::OrderLineSimulator',
      order: 1,
      parentName: '::Simulators',
      requiredPolicy: AppPermissionConsts.ExternalDefault,
      iconClass: 'pi pi-sliders-h',
      eLayoutType: eLayoutType.application,
    },
  ];
  return [
    {
      path: AppPathConsts.CreateOrderLineSimulator,
      name: '::Simulators',
      iconClass: 'pi pi-sparkles',
      order: parentOrder,
      requiredPolicy: AppPermissionConsts.ExternalPermission,
    },
    ...children,
  ];
}
